import { useMemo, useState, type ReactNode } from "react";
import { version as appVersion } from "../../package.json";
import { AccountModal } from "./AccountModal";
import { useAuth } from "./AuthContext";
import { appConfig } from "./config";
import { navigate, useAppRoute } from "./routes";
import { collectPermissions } from "../shared/lib/permissions";
import { Icon, type IconName } from "../shared/ui/Icon";
import { IconButton } from "../shared/ui/Primitives";

interface NavItem {
  route: "chat" | "knowledge" | "retrieval" | "users" | "kd" | "kd-normative" | "kd-materials";
  label: string;
  icon: IconName;
  permission?: string;
}

const navigation: NavItem[] = [
  { route: "chat", label: "Чат", icon: "chat" },
  { route: "kd", label: "Проверка КД", icon: "check", permission: "kd.use" },
  { route: "kd-materials", label: "Материалы КД", icon: "database", permission: "kd.use" },
  { route: "kd-normative", label: "Нормативная база КД", icon: "database", permission: "kd.use" },
  { route: "knowledge", label: "База знаний", icon: "database", permission: "knowledge.view" },
  { route: "retrieval", label: "Лаборатория RAG", icon: "flask", permission: "settings.view" },
  { route: "users", label: "Пользователи", icon: "users", permission: "users.manage" },
];

export function Shell({ children }: { children: ReactNode }) {
  const { user, session, logout } = useAuth();
  const route = useAppRoute();
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const available = navigation.filter((item) => !item.permission || permissions.has(item.permission));
  const activeRoute = route.name === "kd-sources" || (route.name === "kd" && route.tab === "normative") ? "kd-normative" : route.name;
  const activeItem = navigation.find((item) => item.route === activeRoute) ?? navigation[0]!;
  const displayName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || "Пользователь";

  return (
    <div className={`app-shell ${navCollapsed ? "nav-collapsed" : ""}`}>
      <aside className={`main-nav ${navOpen ? "is-open" : ""}`}>
        <div className="main-nav__brand">
          <span className="brand-mark brand-mark--small">S</span>
          <div><strong>STREAMER</strong><span>AI ASSISTANT</span></div>
          <IconButton
            className="main-nav__collapse"
            icon="panel"
            label={navCollapsed ? "Развернуть меню" : "Свернуть меню"}
            onClick={() => setNavCollapsed((value) => !value)}
          />
          <IconButton className="mobile-only" icon="close" label="Закрыть меню" onClick={() => setNavOpen(false)} />
        </div>
        <div className="main-nav__group-label">Рабочее пространство</div>
        <nav>
          {available.map((item) => (
            <button
              type="button"
              key={item.route}
              className={activeRoute === item.route ? "is-active" : ""}
              title={navCollapsed ? item.label : undefined}
              onClick={() => {
                setNavOpen(false);
                navigate(item.route === "knowledge" ? { name: "knowledge", tab: "cards" } : { name: item.route });
              }}
            >
              <Icon name={item.icon} size={19} /><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="main-nav__footer">
          <button type="button" className="main-nav__profile" onClick={() => setAccountOpen(true)} title="Профиль и способы входа">
            <span className="user-avatar">{(user?.first_name?.[0] ?? "П").toUpperCase()}</span>
            <span><strong>{displayName}</strong><small>{session?.auth_method === "password" ? "Вход по почте" : "Вход через Bitrix24"}</small></span>
            <Icon name="settings" size={17} />
          </button>
          <button type="button" className="main-nav__logout" onClick={() => void logout()} title="Выйти">
            <Icon name="logout" size={18} /><span>Выйти</span>
          </button>
          <small className="main-nav__version">v{appVersion}</small>
        </div>
      </aside>

      {navOpen ? <button className="nav-scrim" aria-label="Закрыть меню" onClick={() => setNavOpen(false)} /> : null}

      <div className="app-frame">
        <header className="topbar">
          <div className="topbar__title">
            <IconButton className="mobile-only" icon="menu" label="Открыть меню" onClick={() => setNavOpen(true)} />
            <div>
              <strong>{activeItem.label}</strong>
              <span>{session?.portal.domain || appConfig.appTitle}</span>
            </div>
          </div>
          <button type="button" className="topbar__account" onClick={() => setAccountOpen(true)}>
            <span>{displayName}</span>
            <span className={`session-dot ${session?.auth_method === "password" ? "is-password" : ""}`} />
          </button>
        </header>
        <main className="workspace">{children}</main>
      </div>

      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />
    </div>
  );
}
